import { memo, useCallback, useState } from 'react';
import { getRectOfNodes, Handle, Node, NodeProps, NodeToolbar, Position, useReactFlow, useStore, useStoreApi } from 'reactflow';
import { NodeResizer } from '@reactflow/node-resizer';

import useDetachNodes from '../../hooks/useDetachNodes';
import { InputHoverState } from '../node.model';
import ExpandToolbar from '../../components/expand-toolbar/expand-toolbar';
import { ResponseState } from '../../components/input.model';
import { ChatNodeData } from '../response-node/response-node.model';
import { createBrainstormNode, createChatNode } from '../response-node/response-node.helper';
import { TypeTopicNode } from '../topic-node/topic-node.model';

import './group-node.scss';
import { extractNodeTexts, predictSubtopics } from '../node.helper';
import { BrainstormNodeData } from '../brainstorm-node/brainstorm-node.model';

const padding = 25;

function GroupNode(props: any) {
  const reactFlowInstance = useReactFlow();
  const store = useStoreApi();
  const detachNodes = useDetachNodes();

  const [toolbarAvailable, setToolbarAvailable] = useState(true);
  const [toolbarViewState, setToolbarViewState] = useState(InputHoverState.OUT);

  // All Child Nodes should be topic nodes
  const { minWidth, minHeight, childNodes, hasChildNodes, topics } = useStore((store) => {
    const childNodes = Array.from(store.nodeInternals.values()).filter((n) => n.parentNode === props.id);
    const rect = getRectOfNodes(childNodes);

    return {
      minWidth: rect.width + padding * 2,
      minHeight: rect.height + padding * 2,
      childNodes: childNodes,
      hasChildNodes: childNodes.length > 0,
      topics: childNodes.map((child): child is TypeTopicNode => child.data.topicName).join(', '),
    };
  }, isEqual);

  const onDelete = () => {
    reactFlowInstance.deleteElements({ nodes: [{ id: props.id }] });
  };

  const onDetach = () => {
    const childNodeIds = Array.from(store.getState().nodeInternals.values())
      .filter((n) => n.parentNode === props.id)
      .map((n) => n.id);

    detachNodes(childNodeIds, props.id);
  };

  return (
    <div
      className='group-node'
      onClick={() => {
        if (toolbarViewState !== InputHoverState.HOVER) {
          setToolbarViewState(InputHoverState.HOVER);
        }
      }}
      // onMouseEnter={() => toolbarViewState === InputHoverState.OUT && setToolbarViewState(InputHoverState.HOVER)}
      onMouseLeave={() => toolbarViewState === InputHoverState.HOVER && setToolbarViewState(InputHoverState.OUT)}
    >
      {hasChildNodes && 
        <NodeToolbar className="node-toolbar nodrag">
          <button onClick={onDetach}>Ungroup</button>
        </NodeToolbar>
      }
      {toolbarAvailable && 
        <NodeToolbar isVisible={toolbarViewState !== InputHoverState.OUT} position={Position.Bottom}>
          <ExpandToolbar
            sourceId={props.id}
            setInputState={setToolbarViewState}
            setToolbarAvailable={setToolbarAvailable}
            topic={extractNodeTexts(childNodes).join(', ')}
          />
        </NodeToolbar>
      }
      <Handle type="source" position={Position.Bottom} className="node-handle-direct source-handle"/>
      <NodeResizer
        lineClassName='group-resize-line'
        handleClassName='group-resize-handle'
        isVisible={toolbarViewState === InputHoverState.HOVER}
        minWidth={minWidth}
        minHeight={minHeight}
      />
    </div>
  );
}

function isEqual(prev: any, next: any) {
  return (
    prev.minWidth === next.minWidth && prev.minHeight === next.minHeight && prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(GroupNode);
